import { useCallback, useEffect, useMemo, useState } from "react";
import { ArrowRight, Briefcase } from "lucide-react";
import {
  Link,
  useLocation,
  useNavigate,
  useSearchParams,
} from "react-router-dom";
import { format } from "date-fns";
import Pagination from "src/components/Pagination";
import Status from "src/components/Status/Status";
import EntityAvatar from "src/components/EntityAvatar";
import PageShell from "src/components/PageShell";
import BackLink from "src/components/BackLink";
import Tabs from "src/components/Tabs";
import { SectionHeader, EmptyState } from "src/components/ui-x";
import useAlert from "src/hooks/use-alert";
import { useMatchedJobsQuery } from "src/lib/endpoints/job/job.api";
import Table from "../../components/Table/Table";

const MatchedJobsTable = () => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const siblingCount = 1;
  const pageColumn = 1;
  const [pageSize, setPageSize] = useState(20);
  const [page, setPage] = useState(0);
  const [searchParams, setSearchParams] = useSearchParams();
  const { matchedJobs, isLoading } = useMatchedJobsQuery(
    page + 1,
    searchParams.get("status") ?? "open"
  );
  const [totalCount, setTotalCount] = useState(0);
  const { state, open, close, Alert } = useAlert();

  const standalone = pathname.includes("job");
  const status = searchParams.get("status") ?? "open";

  const handlePageChange = useCallback(
    (_page: number) => {
      setPage(_page - 1);
    },
    [page]
  );

  const tableHeader = useMemo(
    () => [
      {
        title: "Client",
        key: "client",
      },
      {
        title: "Request",
        key: "request",
      },
      {
        title: "Date",
        key: "date",
      },
      {
        title: "Status",
        key: "status",
      },
      {
        title: "",
        key: "details",
      },
    ],
    []
  );

  const tabHeading = useMemo(
    () => [
      {
        id: 1,
        title: "open",
      },
      {
        id: 2,
        title: "offered",
      },
      {
        id: 3,
        title: "closed",
      },
    ],
    []
  );

  const modifiedData = useMemo(() => {
    if (!matchedJobs?.data) return [];

    return matchedJobs.data.map((each: any) => [
      <div className="flex items-center gap-3">
        <span className="relative">
          <EntityAvatar src={each?.companyLogo} name={each?.companyName} />
          <span className="absolute -right-0.5 bottom-0 h-2.5 w-2.5 rounded-full border-2 border-card bg-emerald-500" />
        </span>
        <span className="font-semibold text-brand-navy">
          {each?.companyName ?? ""}
        </span>
      </div>,
      <span className="capitalize text-muted-foreground">
        {each?.jobTitle ?? ""}
      </span>,
      <span className="tabular-nums text-muted-foreground">
        {format(new Date(each?.created_at ?? Date.now()), "EEE, MMM d")}
      </span>,
      <Status label={each.status} />,
      <button
        type="button"
        className="inline-flex h-8 items-center rounded-md border border-brand-navy px-3 text-xs font-semibold text-brand-navy transition-colors hover:bg-surface"
        onClick={() => {
          navigate(`/jobs/${each?.id ?? ""}`);
        }}
      >
        Details
      </button>,
    ]);
  }, [matchedJobs]);

  useEffect(() => {
    setTotalCount(matchedJobs?.total ?? 0);
  }, [matchedJobs]);

  useEffect(() => {
    if (!searchParams.get("status")) {
      setSearchParams({ status: "open" });
    }
  }, []);

  if (isLoading || matchedJobs === undefined || modifiedData === undefined)
    return null;

  const table = (
    <Table
      header={tableHeader}
      eyebrow={standalone ? undefined : "Opportunities"}
      label={standalone ? undefined : "Matched jobs"}
      actions={
        standalone ? undefined : (
          <Link
            to="/jobs"
            className="inline-flex items-center gap-1 text-xs font-semibold text-brand-orange hover:underline"
          >
            See all
            <ArrowRight className="h-3 w-3" />
          </Link>
        )
      }
      column={modifiedData}
      defaultDisplay={
        <EmptyState
          className="border-0 py-10"
          icon={<Briefcase className="h-6 w-6" />}
          title={`No ${status} matched jobs`}
          description="Complete your profile to improve the jobs we match you with."
        />
      }
    />
  );

  if (!standalone) return table;

  return (
    <PageShell wide>
      <BackLink to="/dashboard" label="Dashboard" />
      <SectionHeader
        eyebrow="Opportunities"
        title="Matched jobs"
        description="Roles we have matched to your profile."
        actions={
          <Tabs
            items={tabHeading}
            value={status}
            onChange={(title) => setSearchParams({ status: title })}
          />
        }
      />

      {table}

      <Pagination
        rangeStart={
          totalCount !== 0 ? (page || 0) * ((pageSize || 0) * pageColumn) + 1 : 0
        }
        totalCount={totalCount}
        currentPage={(page || 0) + 1}
        pageSize={pageSize}
        pageColumn={pageColumn}
        siblingCount={siblingCount}
        onPageChange={handlePageChange}
      />

      <span className="fixed right-3 top-3 z-30">
        <Alert />
      </span>
    </PageShell>
  );
};

export default MatchedJobsTable;